/**
 * FavoritesPage — Full favorites management page
 * TRNS-151 sub-task 4: FavoritesPage + route wiring
 *
 * Shows FavoritesList for authenticated users, login prompt otherwise.
 * Supports add, edit, delete, and "select to translate" flows.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { FavoritesList, PhraseForm } from '../components/FavoritePhrases';
import type { PhraseFormData } from '../components/FavoritePhrases';
import { favoritesService } from '../services/favoritesService';
import { FavoritePhrase } from '../types';
import './FavoritesPage.css';

export const FavoritesPage: React.FC = () => {
  const { user, loading: authLoading, signInWithGoogle, signInWithGitHub, signInWithMicrosoft } = useAuth();
  const navigate = useNavigate();

  const [phrases, setPhrases] = useState<FavoritePhrase[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingPhrase, setEditingPhrase] = useState<FavoritePhrase | undefined>(undefined);

  // Delete confirmation
  const [deletingPhrase, setDeletingPhrase] = useState<FavoritePhrase | null>(null);

  const loadFavorites = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      const data = await favoritesService.getFavorites(user.id);
      setPhrases(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load favorites');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadFavorites();
    }
  }, [user, loadFavorites]);

  const handleSelect = (phrase: FavoritePhrase) => {
    // Increment usage and navigate to app page with the phrase text
    favoritesService.incrementUsage(phrase.id).catch(console.error);
    // Store selected phrase in session storage for TranslationForm to pick up
    sessionStorage.setItem('translayshun_favorite_text', phrase.text);
    navigate('/app');
  };

  const handleAdd = () => {
    setEditingPhrase(undefined);
    setShowForm(true);
  };

  const handleEdit = (phrase: FavoritePhrase) => {
    setEditingPhrase(phrase);
    setShowForm(true);
  };

  const handleDelete = (phrase: FavoritePhrase) => {
    setDeletingPhrase(phrase);
  };

  const confirmDelete = async () => {
    if (!deletingPhrase) return;

    try {
      await favoritesService.deleteFavorite(deletingPhrase.id);
      setDeletingPhrase(null);
      await loadFavorites();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete phrase');
    }
  };

  const handleFormSubmit = async (data: PhraseFormData) => {
    if (editingPhrase) {
      await favoritesService.updateFavorite(editingPhrase.id, {
        label: data.label,
        text: data.text,
        targetLang: data.targetLang,
        tags: data.tags,
      });
    } else {
      await favoritesService.addFavorite({
        label: data.label,
        text: data.text,
        targetLang: data.targetLang,
        tags: data.tags,
      });
    }

    setShowForm(false);
    setEditingPhrase(undefined);
    await loadFavorites();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingPhrase(undefined);
  };

  // Auth loading state
  if (authLoading) {
    return (
      <div className="favorites-page">
        <div className="favorites-page__loading">Loading...</div>
      </div>
    );
  }

  // Unauthenticated — show login prompt
  if (!user) {
    return (
      <div className="favorites-page">
        <div className="favorites-page__login-prompt">
          <h2>Favorite Phrases</h2>
          <p>Sign in to save and manage your favorite phrases for quick translations.</p>
          <div className="favorites-page__login-buttons">
            <button className="favorites-page__login-btn" onClick={signInWithGoogle}>
              Sign in with Google
            </button>
            <button className="favorites-page__login-btn" onClick={signInWithGitHub}>
              Sign in with GitHub
            </button>
            <button className="favorites-page__login-btn" onClick={signInWithMicrosoft}>
              Sign in with Microsoft
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="favorites-page">
      <header className="favorites-page__header">
        <h1>Favorite Phrases</h1>
        <p className="favorites-page__subtitle">
          Your saved phrases for quick one-tap translations.
        </p>
      </header>

      {error && (
        <div className="favorites-page__error" role="alert">
          <p>{error}</p>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* Delete confirmation dialog */}
      {deletingPhrase && (
        <div className="favorites-page__dialog-overlay">
          <div className="favorites-page__dialog">
            <h3>Delete Phrase</h3>
            <p>
              Are you sure you want to delete "{deletingPhrase.label}"? This cannot be undone.
            </p>
            <div className="favorites-page__dialog-actions">
              <button
                className="favorites-page__dialog-btn favorites-page__dialog-btn--cancel"
                onClick={() => setDeletingPhrase(null)}
              >
                Cancel
              </button>
              <button
                className="favorites-page__dialog-btn favorites-page__dialog-btn--delete"
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit form overlay */}
      {showForm && (
        <div className="favorites-page__form-overlay">
          <div className="favorites-page__form-container">
            <PhraseForm
              phrase={editingPhrase}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
            />
          </div>
        </div>
      )}

      {loading ? (
        <div className="favorites-page__loading">Loading favorites...</div>
      ) : (
        <FavoritesList
          phrases={phrases}
          onSelect={handleSelect}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onAdd={handleAdd}
        />
      )}
    </div>
  );
};
